// Phase 78az / 78ba (Feb-2026) — Tiny shared store + hook for Google API
// feature flags + a fire-and-forget usage tracker.
//
// `useGoogleApiFlag(key)` — React hook (re-renders when admin saves).
// `getGoogleApiFlag(key)`  — Sync read for non-hook callsites.
// `trackGoogleApiUsage(key)` — POST to /api/google-api-usage/track so the
//                              admin's usage chart reflects this call.
//                              Fire-and-forget (no await, no retry).
import { useEffect, useState } from 'react';
import api from '../lib/api';

const DEFAULTS = {
  polyline_booking_live_tracker: false,
  polyline_admin_live_tracking: false,
  polyline_trip_monitoring_map: false,
  polyline_live_trip_modal: false,
  // Phase 78bb — one-shot Google ETA at trip booking.  This is the ONLY
  // place we still call Directions API for ETA; everything else now reads
  // from the cached value on the trip doc.
  eta_oneshot_at_booking: true,
  // Legacy live recompute flags — default OFF.  Admin can flip back on
  // for forensic debugging.
  eta_booking_live_tracker: false,
  eta_admin_live_tracking_anchor: false,
  eta_chained_pilot_queue: false,
  eta_pool_allocation: false,
  optimize_stops_booking: true,
  optimize_stops_admin: true,
  places_autocomplete_supplier_picker: true,
  geocoding_supplier_coord_health: true,
};

let cache = { ...DEFAULTS };
let loaded = false;
let inflight = null;
const subscribers = new Set();

async function load(force = false) {
  if (loaded && !force) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data } = await api.get('/google-api-flags');
      cache = { ...DEFAULTS, ...(data.values || {}) };
      loaded = true;
    } catch {
      // On failure keep defaults — never fail loudly so map components
      // still render.
    } finally {
      inflight = null;
      subscribers.forEach(fn => fn(cache));
    }
    return cache;
  })();
  return inflight;
}

export function refreshGoogleApiFlags() { return load(true); }
export function getGoogleApiFlag(key) { return cache[key] ?? DEFAULTS[key] ?? true; }

export function useGoogleApiFlags() {
  const [flags, setFlags] = useState(cache);
  useEffect(() => {
    let mounted = true;
    const onChange = (v) => { if (mounted) setFlags({ ...v }); };
    subscribers.add(onChange);
    load().then(v => { if (mounted) setFlags({ ...v }); });
    return () => { mounted = false; subscribers.delete(onChange); };
  }, []);
  return flags;
}

export function useGoogleApiFlag(key) {
  const flags = useGoogleApiFlags();
  return flags[key] ?? DEFAULTS[key] ?? true;
}

// ----- USAGE TRACKING -------------------------------------------------
// Fire-and-forget tracker.  Backend stores per-day counts that feed the
// usage chart.  Never await this — and never let it throw.
export function trackGoogleApiUsage(flag_key, count = 1) {
  try {
    api.post('/google-api-usage/track', { flag_key, count }).catch(() => {});
  } catch { /* noop */ }
}
